import LoadingOther from "@/components/index/fragments/LoadingOther";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { getLatestBlogPosts } from "@/lib/blog";
import { formatCzDate } from "@/lib/date";
import { ArrowRightIcon, UserIcon } from "@heroicons/react/24/solid";
import { motion } from "framer-motion";
import Link from "next/link";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { BlogInitialLoadingGate } from "@/components/sections/BlogInitialLoadingGate";
import type { Blog as BlogType } from "@/types";

type BlogProps = {
  limit: number;
  isPage?: boolean;
};

export default function Blog({ limit, isPage = false }: BlogProps) {
  const { blog } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      label: "Průvodce realitami",
      title: "Tipy & novinky ze světa nemovitostí",
      subtitle:
        "Vše, co potřebujete vědět před koupí, prodejem nebo pronájmem.",
      read: "Číst ->",
      moreTitle: "Zobrazit další články",
      moreSubtitle: "Objevte další tipy a inspiraci",
    },
    en: {
      label: "Real estate guide",
      title: "Tips & news from the property world",
      subtitle:
        "Everything you need to know before buying, selling, or renting.",
      read: "Read ->",
      moreTitle: "Show more articles",
      moreSubtitle: "Discover more tips and inspiration",
    },
  }[language];

  if (isPage) {
    return (
      <BlogInitialLoadingGate>
        <BlogContent blog={blog} limit={limit} isPage={isPage} copy={copy} />
      </BlogInitialLoadingGate>
    );
  }

  if (!blog) return <LoadingOther />;

  return <BlogContent blog={blog} limit={limit} isPage={isPage} copy={copy} />;
}

function BlogContent({
  blog,
  limit,
  isPage,
  copy,
}: {
  blog: BlogType | null;
  limit: number;
  isPage: boolean;
  copy: {
    label: string;
    title: string;
    subtitle: string;
    read: string;
    moreTitle: string;
    moreSubtitle: string;
  };
}) {
  if (!blog) return null;

  const items = getLatestBlogPosts(blog, limit || undefined);
  const showMoreButton = limit > 0;

  return (
    <div id="pruvodce" className="blog-root">
      <Panel variant="shell">
        <div className="blog-header !mb-8">
          <span className="section-label">{copy.label}</span>
          {isPage ? (
            <h1 className="page-headline text-softiq-copy dark:text-softiq-copy-inverse">
              {copy.title}
            </h1>
          ) : (
            <h2 className="section-title text-softiq-copy dark:text-softiq-copy-inverse">
              {copy.title}
            </h2>
          )}
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>

        <div className="blog-grid">
          {items.map((post, index) => (
            <Link
              href={`/blog/${post.url}`}
              prefetch={false}
              key={post.id}
              className={`blog-card ${index === 0 ? "blog-card--featured" : ""}`}
            >
              <div className="blog-card-body">
                {(post._seo?.meta_keywords ?? []).length > 0 && (
                  <div className="blog-tags">
                    {(post._seo?.meta_keywords ?? [])
                      .slice(0, 2)
                      .map((tag, idx) => (
                        <span key={idx} className="blog-tag">
                          {tag}
                        </span>
                      ))}
                  </div>
                )}

                <h3 className="blog-card-title">{post.title}</h3>

                <p className="blog-card-summary">
                  {(post.summary ?? post.content_html ?? "")
                    .replace(/<[^>]*>/g, "")
                    .slice(0, 700)
                    .trim()}
                  …
                </p>

                <div className="blog-card-footer">
                  <div className="blog-card-meta">
                    <UserIcon className="blog-card-meta-icon" />
                    <span>{blog.author}</span>
                    {post.date_modified && (
                      <>
                        <span className="blog-card-meta-sep">·</span>
                        <span>
                          {formatCzDate(post.date_modified, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </>
                    )}
                  </div>
                  <span className="blog-card-cta">{copy.read}</span>
                </div>
              </div>
            </Link>
          ))}
          {showMoreButton && (
            <div className="flex items-center justify-center">
              <Button variant="default" className="rounded-[20px] p-8">
                <Link href="/blog/" prefetch={false}>
                  <motion.div whileTap={{ scale: 0.98 }}>
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ x: [0, 6, 0] }}
                        transition={{ repeat: Infinity, duration: 1.2 }}
                        className="flex h-12 w-12 items-center justify-center rounded-[12px] bg-white/20 text-2xl"
                      >
                        <ArrowRightIcon className="size-6 text-white" />
                      </motion.div>

                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {copy.moreTitle}
                        </h3>
                        <p className="text-sm text-white">
                          {copy.moreSubtitle}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              </Button>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
